import React, { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Alert, Box, Button } from '@mui/material';

import TabPanelShell from '~/components/Offers/Detail/OfferBuilder/shared/TabPanelShell';

import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';

import {
  useEgsDraftsContext,
  usePackageOptionsContext,
  usePricingToolSession,
  usePropertySetupContext,
} from '../../PricingToolSessionContext';

import EgsTable from './EgsTable';
import EgsWizardModal from './EgsWizardModal';
import { egsRooms, knownEgsRooms } from './egsModel';
import type { EgsDraft, EgsDraftInput } from './types';

interface Props {
  offerId?: string;
}

export default function EgsTab({ offerId }: Props) {
  const { data, error: relatedError, isLoading } = useGetOpportunityRelated(offerId);
  const { addRecords, drafts, removeDraft } = useEgsDraftsContext();
  const { roomAssets, optionState } = usePackageOptionsContext();
  const { draftIdBySfid } = usePricingToolSession();
  const { enqueueSnackbar } = useSnackbar();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingDraft, setEditingDraft] = useState<EgsDraft | undefined>(undefined);

  const handleEdit = (draft: EgsDraft) => {
    setEditingDraft(draft);
    setWizardOpen(true);
  };

  const handleCloseWizard = () => {
    setWizardOpen(false);
    setEditingDraft(undefined);
  };

  const handleCommit = (records: Array<EgsDraftInput>) => {
    if (editingDraft) {
      removeDraft(editingDraft.id);
    }
    addRecords(records);
    enqueueSnackbar(
      records.length
        ? `Saved ${records.length} extra guest surcharge${records.length === 1 ? '' : 's'}`
        : 'No extra guest surcharges added',
      { variant: records.length ? 'success' : 'info' },
    );
    setEditingDraft(undefined);
  };

  const {
    fx: { currency, audToSourceCurrencyRate, isLoading: isFxRateLoading },
  } = usePropertySetupContext();
  const rooms = useMemo(() => egsRooms(optionState.roomsByCategory), [optionState.roomsByCategory]);
  const namedRooms = useMemo(
    () => knownEgsRooms(data, optionState.roomsByCategory, roomAssets, draftIdBySfid),
    [data, optionState.roomsByCategory, roomAssets, draftIdBySfid],
  );

  if (isLoading) {
    return null;
  }

  return (
    <TabPanelShell
      actions={
        <Button
          disabled={!rooms.length}
          onClick={() => setWizardOpen(true)}
          variant="contained"
          sx={{ whiteSpace: 'nowrap' }}
        >
          Configure EGS
        </Button>
      }
      subtitle="Configure surcharges for extra infants, children, and adults beyond included guests. This still saves to your session only - creating the record in Salesforce needs a backend endpoint that doesn't exist yet, so nothing here is submitted until that lands."
      title="Extra Guest Surcharges"
    >
      {relatedError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Unable to load opportunity data, so the surcharge currency may be inaccurate.
        </Alert>
      )}

      <Box>
        <EgsTable drafts={drafts} rooms={namedRooms} onEdit={handleEdit} onRemove={removeDraft} />
      </Box>

      <EgsWizardModal
        audToSourceCurrencyRate={audToSourceCurrencyRate}
        currency={currency}
        editingDraft={editingDraft}
        isFxRateLoading={isFxRateLoading}
        onClose={handleCloseWizard}
        onCommit={handleCommit}
        open={wizardOpen}
        rooms={rooms}
      />
    </TabPanelShell>
  );
}
